import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { Capacitor } from '@capacitor/core';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';

export const useNotificationPermissions = () => {
  const [notifyBeforeTrial, setNotifyBeforeTrial] = useState(false);
  const [isNotificationBlockedModalOpen, setIsNotificationBlockedModalOpen] = useState(false);

  const handleEnableNotifications = useCallback(async () => {
    try {
      let permission: string;

      // Check current permission status - use platform-specific API
      if (Capacitor.isNativePlatform()) {
        // Check if FirebaseMessaging plugin is available on native
        if (!Capacitor.isPluginAvailable('FirebaseMessaging')) {
          toast.error('Notifications are not available on this device.');
          return;
        }
        const { receive } = await FirebaseMessaging.checkPermissions();
        permission = receive;
      } else {
        // Web platform - use browser Notification API
        if (typeof Notification === 'undefined') {
          toast.error('Notifications are not supported in this browser.');
          return;
        }
        permission = Notification.permission;
      }

      if (permission === 'granted') {
        // Permissions already granted, enable the toggle
        setNotifyBeforeTrial(true);
      } else if (permission === 'denied') {
        // Permissions were previously denied, show modal to guide user to settings
        setIsNotificationBlockedModalOpen(true);
        return;
      } else {
        // First time requesting permissions (permission === 'prompt' or 'default')
        let newPermission: string;

        if (Capacitor.isNativePlatform()) {
          const permissionResult = await FirebaseMessaging.requestPermissions();
          newPermission = permissionResult.receive;
        } else {
          // Web platform - use browser Notification API
          newPermission = await Notification.requestPermission();
        }

        if (newPermission === 'granted') {
          setNotifyBeforeTrial(true);
        } else {
          // User denied the permission prompt, show modal to guide them to settings
          setIsNotificationBlockedModalOpen(true);
          return;
        }
      }
    } catch (error) {
      console.error('Notification permission error:', error);
      toast.error('Unable to enable notifications. Please try again.');
    }
  }, []);

  const handleToggle = useCallback(() => {
    if (notifyBeforeTrial) {
      setNotifyBeforeTrial(false);
    } else {
      handleEnableNotifications();
    }
  }, [notifyBeforeTrial, handleEnableNotifications]);

  return {
    notifyBeforeTrial,
    handleToggle,
    isNotificationBlockedModalOpen,
    setIsNotificationBlockedModalOpen,
  };
};
